import moment from 'moment';
import React, { useState } from 'react';
import { isAndroid, isIOS } from 'react-device-detect';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ApolloError } from '@apollo/client';
import { TimetableRuleState } from '@chirp/access-verification';
import useInterval from '@use-it/interval';

import {
  AccessPointFragment as AccessPoint,
  GuestPassFragment as UserRole,
  useanonymousUserRolesSubscription,
  usegetAccessPointQuery,
  useunlockAccessPointMutation,
} from '../../apollo';
import { font } from '../../assets/styles';
import { Button, Footer, Hero, LoadingOverlay, Modal, QRScanner, RequestAccessButtons } from '../../components';
import { HeroProps } from '../../components/Hero';
import { ModalProps } from '../../components/Modal';
import TermsOfUseModal from '../../components/TermsOfUseModal';
import { CHECK_ACCESS_INTERVAL_MS } from '../../config';
import { getErrorMessage } from '../../helpers';
import { useAcceptedTermsAt, useAnonymousUserId } from '../../hooks';
import {
  getAvailabilitySchedule,
  getAvailabilityState,
  getLockdownEndDateString,
  isDateAllowedOnTimetable,
  renderParagraph,
} from '../../utils';
import styles from './styles';
import WhiteSearchIcon from '../../assets/icons/search-white.png';

export interface EntryConfig {
  heroStatus: HeroProps['status'];
  header: string;
  subHeader?: string | React.ReactNode
  | ((timetable: AccessPoint['timetable'], timezone: AccessPoint['property']['timezone']) => string | React.ReactNode);
  showTime?: boolean;
  showQRToggle?: boolean;
  showAllAvailableSchedule?: boolean;
}

interface ModalState extends Pick<ModalProps, 'showAtBottom' | 'title' | 'message' | 'buttons' | 'show'> {}

interface EntryState {
  activeUserRole: UserRole | null | undefined;
  userRoles: UserRole[];
  sentRequest: boolean;
  modalState: ModalState;
}

function selectActiveUserRole(userRoles: UserRole[]) {
  return userRoles.find(({ expiresAt }) => {
    if (!expiresAt) {
      return null;
    }

    return moment(new Date(expiresAt)).isAfter(moment(new Date()));
  });
}

const Entry: React.FC = () => {
  const accessPointId = useParams().accessPointId as string;
  const anonymousUserId = useAnonymousUserId();
  const [showQRScanner, setShowQRScanner] = useState<boolean>(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [acceptedTermsAt, setAcceptedTermsAt] = useAcceptedTermsAt();
  const [redirectRoute, setRedirectRoute] = useState('');
  const navigate = useNavigate();

  const [state, setState] = useState<EntryState>({
    activeUserRole: null,
    userRoles: [],
    sentRequest: false,
    modalState: {
      show: false,
      showAtBottom: true,
      title: '',
      message: '',
      buttons: [],
    },
  });

  const { activeUserRole, sentRequest, modalState } = state;
  const [unlockAccessPointMutation] = useunlockAccessPointMutation();

    const {
    loading: getCallboxKillerAccessPointLoading,
    data: accessPointsData,
  } = usegetAccessPointQuery({
    skip: !accessPointId,
    variables: { accessPointId },
    onError: (err) => {
      setState(prevState => ({
        ...prevState,
        modalState: {
          show: true,
          showAtBottom: true,
          title: 'Unable to access entry point',
          message: getErrorMessage(err),
        },
      }));
    },
  });

  const accessPoint = accessPointsData ? accessPointsData.accessPoint : null;
  const propertyId = accessPoint ? accessPoint.propertyId : null;
  const propertyFeatureFlags = accessPoint?.property?.featureFlags || [];
  const propertyVirtualDirectory = accessPoint?.property?.propertyVirtualDirectory;
  const isVirtualDirectoryEnabled = (
    propertyFeatureFlags.includes('VIRTUAL_DIRECTORY') &&
    !!propertyVirtualDirectory?.published
  );

  useanonymousUserRolesSubscription({
    skip: !anonymousUserId || !accessPoint,
    onSubscriptionData: ({ subscriptionData }) => {
      const approvedGuestPasses = (subscriptionData.data
        ? subscriptionData.data.anonymousUserRoles
        : []).filter(g => g.scopedPropertyId === propertyId);

      if (approvedGuestPasses) {
        setState(prevState => ({
          ...prevState,
          activeUserRole: selectActiveUserRole(approvedGuestPasses),
          userRoles: approvedGuestPasses,
        }));
      }
    },
  });

  // Check every interval whether the user still has a valid user role
  useInterval(() => {
    setState((prevState) => {
      const nextActiveUserRole = selectActiveUserRole(prevState.userRoles);

      const accessExpired = Boolean(prevState.activeUserRole && !nextActiveUserRole);
      const nextModalState: ModalState = accessExpired
        ? {
          show: true,
          showAtBottom: true,
          title: 'Access Expired',
          message: 'Your guest access has expired. Please send another request for access.',
        }
        : prevState.modalState;

      return {
        ...prevState,
        activeUserRole: nextActiveUserRole,
        // When access token expires, the "Request Sent" should not be shown
        sentRequest: nextActiveUserRole ? false : prevState.sentRequest,
        modalState: nextModalState,
      };
    });

  }, CHECK_ACCESS_INTERVAL_MS);

  if (getCallboxKillerAccessPointLoading) {
    return <LoadingOverlay />;
  }

  if (!accessPointsData) {
    return <Modal
      show
      title={state.modalState.title}
      message={state.modalState.message}
    />;
  }

  if (accessPoint) {
    const { name, image, property, timetable, publicAccessPointRoles } = accessPoint;
    const { timezone } = property;

    const cloudId = image ? image.cloudId : null;

    const accessTime = new Date();

    const availabilityState = getAvailabilityState(accessTime, timetable, timezone);

    const isAccessPointAvailable = isDateAllowedOnTimetable(new Date(), timetable, timezone);
    const requestNotSent = Boolean(!activeUserRole && !sentRequest && anonymousUserId);
    const requestPending = !activeUserRole && sentRequest;
    const { GUEST_PROPERTY_KEY: guestRole, VENDOR_PASS: vendorPassRole } = publicAccessPointRoles;

    const getConfig = (): EntryConfig | null => {
      switch (availabilityState) {
        case TimetableRuleState.CONTROLLED:
          if (!isAccessPointAvailable) {
            return {
              heroStatus: 'denied',
              header: 'This entry point is closed right now.',
              subHeader: '',
              showTime: true,
            };
          }

          if (requestNotSent) {
            return {
              heroStatus: 'none',
              header: `Welcome to ${property.name}!`,
              subHeader: () => {
                 if (!guestRole.hasAccess && vendorPassRole.hasAccess) {
                    return <>
                      {renderParagraph('This entry is for vendors only. Enter your vendor passcode by selecting the option below.')}
                    </>
                  } else if (isVirtualDirectoryEnabled) {
                    return <>
                      {renderParagraph('Guests can request access from a resident by choosing one of the options below.')}
                    </>
                  } else {
                    return <>
                      Visitors can request
                      <span style={{ fontWeight: font.weight.medium }}> 15 minutes of property only access </span>
                      {renderParagraph('from a resident by clicking the link below.')}
                    </>
                  }
              },
            };
          }

          if (requestPending) {
            return {
              heroStatus: 'sending',
              header: 'Request Sent!',
              subHeader: renderParagraph('Do not close this page or you\'ll have to send your request again.'),
            };
          }

          if (activeUserRole && activeUserRole.expiresAt) {
            return {
              heroStatus: 'granted',
              header: 'Request Approved!',
              subHeader: (
                <>
                  {renderParagraph('Tap the top button below to unlock entry points or tap the bottom button to scan another QR Code.')}
                  <div className={styles.access}>{renderParagraph('You have access until:')}</div>
                  <div className={styles.time}>
                    {moment(activeUserRole.expiresAt).format('h:mm A')}
                  </div>
                  <Button
                    className={styles.button}
                    expand
                    type="primary"
                    size="large"
                    onClick={async () => {
                      try {
                        await unlockAccessPointMutation({
                          variables: { accessPointId: accessPoint.accessPointId },
                        });
                      } catch (error) {
                        const castError = error as ApolloError | Error;

                        setState(prevState => ({
                          ...prevState,
                          modalState: {
                            show: true,
                            showAtBottom: false,
                            title: 'Unable to unlock entry point',
                            message: getErrorMessage(castError),
                          },
                        }));
                      }
                    }}
                  >
                    Unlock {name}
                  </Button>
                  <Button
                    className={styles.button}
                    expand
                    type="primary"
                    size="large"
                    onClick={() => setShowQRScanner(true)}
                  >
                    Scan Another QR Code
                  </Button>
                  {showQRScanner && (
                    <QRScanner
                      headerText={activeUserRole?.expiresAt ? moment(activeUserRole.expiresAt).format('h:mm A') : ''}
                      headerSubtext="Access Expires"
                      onClickBack={() => {
                        setShowQRScanner(false);
                      }}
                    />
                  )}
                </>
              ),
              showQRToggle: true,
            };
          }

          return null;
        case TimetableRuleState.LOCKDOWN:
          return {
            heroStatus: 'denied',
            header: 'This entry point is closed right now',
            subHeader: (timetable, timezone) => renderParagraph(
              getLockdownEndDateString(new Date(), timetable, timezone),
            ),
          };
        case TimetableRuleState.UNLOCKED:
          return {
            heroStatus: 'unlocked',
            header: `Welcome to ${property.name}!`,
            subHeader: 'This entry point is unlocked right now.',
            showAllAvailableSchedule: true,
          };
        default:
          return null;
      }
    };

    const config = getConfig();

    if (!config) return null;

    const disabled = !isAccessPointAvailable || (isAccessPointAvailable && availabilityState === TimetableRuleState.UNLOCKED);

    return (
      <>
        <Modal
          show={modalState.show}
          showAtBottom={modalState.showAtBottom}
          buttons={[{
            text: 'Dismiss',
            type: 'primary',
            size: 'large',
            onClick: () => {
              setState({
                ...state,
                modalState: {
                  ...state.modalState,
                  show: false,
                },
              });
            },
          }]}
          {...modalState}
        />
        <div className={styles.container}>
          <div>
            <Hero
              title={accessPoint.name}
              subtitle={property.name}
              cloudId={cloudId}
              imageAlt={accessPoint.name}
              status={config.heroStatus}
            />
            <div className={styles.header}>
              {config.header}
            </div>
            {config.subHeader && (
              <div className={styles.subHeader}>
                {
                  typeof config.subHeader === 'function'
                    ? config.subHeader(accessPoint.timetable, timezone)
                    : config.subHeader
                }
              </div>
            )}
            {config.showTime &&
            accessPoint.timetable &&
              getAvailabilitySchedule(new Date(), accessPoint.timetable, timezone).length > 0 && (
                <div className={styles.scheduleWrapper}>
                  {`${accessPoint.name} Hours`}
                  {getAvailabilitySchedule(new Date(), accessPoint.timetable, timezone)
                    .map((schedule, i) => (
                      <div key={i} className={styles.schedule}>
                        {schedule}
                      </div>
                    ))}
                </div>
              )}
            {/* {config.showAllAvailableSchedule &&
              <AvailableScheduleInfo accessPoint={accessPoint}/>
            } */}
          </div>
          {!activeUserRole && !sentRequest && anonymousUserId && (
            <div className={styles.bottomWrapper}>
              <RequestAccessButtons
                accessPoint={accessPoint}
                showButtons={requestNotSent}
                disableRequestButton={disabled}
                onRequested={() => {
                  setState({
                    ...state,
                    sentRequest: true,
                  });
                }}
              />
              {guestRole.hasAccess && isVirtualDirectoryEnabled && isAccessPointAvailable &&
                <Link
                  onClick={(e) => {
                    if (!acceptedTermsAt) {
                      e.preventDefault();
                      setShowTermsModal(true)
                      setRedirectRoute(`/property-directory/${accessPoint.accessPointId}`);
                    }
                  }}
                  style={{ textDecoration: 'none' }}
                  to={`/property-directory/${accessPoint.accessPointId}`}
                >
                  <Button
                    className={styles.button}
                    type="primary"
                    size='large'
                    disabled={disabled}
                    expand
                    onClick={() => {}}
                  >
                    Property Directory
                    <img src={WhiteSearchIcon} alt="" className={styles.icon20}  />
                  </Button>
                </Link>
              }
              {vendorPassRole.hasAccess && isAccessPointAvailable &&
                <Link
                  onClick={(e) => {
                    if (!acceptedTermsAt) {
                      e.preventDefault();
                      setShowTermsModal(true)
                      setRedirectRoute(`/vendor/${accessPoint.accessPointId}`);
                    }
                  }}
                  style={{ textDecoration: 'none' }}
                  to={`/vendor/${accessPoint.accessPointId}`}
                >
                  <Button
                    className={styles.button}
                    type="primary"
                    size='large'
                    disabled={disabled}
                    expand
                    onClick={() => {}}
                  >
                    Vendor Passcode
                    <span className="p text-white" style={{
                      fontSize: 23,
                      marginLeft: 10,
                    }}>#</span>
                  </Button>
                </Link>
              }

              {(isIOS || isAndroid) && (
                <Button
                  className={styles.button}
                  type="outline"
                  size="large"
                  expand
                  onClick={() => {
                    if (isIOS) { window.open(
                      'https://apps.apple.com/us/app/communityconnect-stratis/id1482867892',
                      '_blank',
                      'noreferrer',
                    );
                    }
                    if (isAndroid) { window.open(
                      'https://play.google.com/store/apps/details?id=com.stratisiot.mobile',
                      '_blank',
                      'noreferrer',
                    );
                    }
                  }}
                >
                  I am a Resident
                </Button>
              )}
            </div>
          )}
          <TermsOfUseModal
            isOpen={showTermsModal}
            closeModal={() => setShowTermsModal(false)}
            onAcceptTerms={async () => {
              setShowTermsModal(false);
              setAcceptedTermsAt(moment.utc(new Date()).toISOString());
              navigate(redirectRoute)
            }}
          />
        </div>
        <Footer />
      </>
    );
  }

  return <LoadingOverlay />
};

export default Entry;
